import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { getAuth, signInWithEmailAndPassword, signOut, onAuthStateChanged } from "firebase/auth";
import { app } from '../firebase';
import './AdminDashboard.css';

const auth = getAuth(app);
const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";

const AdminDashboard = () => {
  const [user, setUser] = useState(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [schedules, setSchedules] = useState([]);
  const [newSchedule, setNewSchedule] = useState({ day: 5, time: '18:00', prize: '$7 USD' });
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  // Auth State Listener
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setUser(user);
      if (user) {
        fetchData(user);
      }
    });
    return () => unsubscribe();
  }, []);

  const fetchData = async (user) => {
    try {
      const token = await user.getIdToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      const schedulesRes = await axios.get(`${API_URL}/api/admin/schedules`, config);
      setSchedules(schedulesRes.data);

      const logsRes = await axios.get(`${API_URL}/api/admin/logs`, config);
      setLogs(logsRes.data);
    } catch (error) {
      console.error("Failed to fetch admin data", error);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch (error) {
      setMessage('Login failed: ' + error.message);
    }
  };

  const handleLogout = async () => {
    await signOut(auth);
  };

  const handleAddSchedule = async () => {
    setLoading(true);
    try {
      const token = await user.getIdToken();
      await axios.post(`${API_URL}/api/admin/schedules`, newSchedule, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessage('Schedule added successfully!');
      fetchData(user);
    } catch (error) {
      const errMsg = error.response?.data?.error || 'Failed to add schedule';
      setMessage(errMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSchedule = async (id) => {
    if (!window.confirm("Are you sure you want to delete this raffle?")) return;
    try {
        const token = await user.getIdToken();
        await axios.delete(`${API_URL}/api/admin/schedules/${id}`, {
            headers: { Authorization: `Bearer ${token}` }
        });
        fetchData(user);
    } catch (error) {
        alert("Failed to delete schedule");
    }
  };

  const handleClearLogs = async () => {
    if (!window.confirm("Are you sure you want to CLEAR ALL winner logs? This cannot be undone.")) return;
    try {
        const token = await user.getIdToken();
        await axios.delete(`${API_URL}/api/admin/logs`, {
            headers: { Authorization: `Bearer ${token}` }
        });
        setMessage('Logs cleared successfully!');
        fetchData(user);
    } catch (error) {
        setMessage('Failed to clear logs');
    }
  };

  if (!user) {
    return (
      <div className="admin-login-container">
        <Link to="/" className="home-link" style={{position: 'absolute', top: '20px', left: '20px'}}>
            Home
        </Link>
        <div className="admin-login-card">
            <h2>Admin Access</h2>
            <form onSubmit={handleLogin}>
                <input 
                    type="email" 
                    placeholder="Email" 
                    value={email} 
                    onChange={e => setEmail(e.target.value)} 
                    required 
                />
                <input 
                    type="password" 
                    placeholder="Password" 
                    value={password} 
                    onChange={e => setPassword(e.target.value)} 
                    required 
                />
                <button type="submit">Login</button>
            </form>
            {message && <p className="error-msg">{message}</p>}
        </div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <Link to="/" className="home-link" style={{position: 'absolute', top: '20px', left: '20px', padding: '8px 12px', fontSize: '0.9rem'}}>
          Home
      </Link>
      <header className="admin-header">
        <h1>Raffle Admin Panel</h1>
        <button onClick={handleLogout} className="logout-btn">Logout</button>
      </header>

      <div className="admin-content">
        {/* Settings Section */}
        <section className="admin-section">
            <h2>Raffle Schedules</h2>
            
            {/* List of Schedules */}
            <div className="schedules-list">
                {schedules.map(schedule => (
                    <div key={schedule.id} className="schedule-item">
                        <div className="schedule-info">
                            <span className="schedule-day">
                                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][schedule.day]}
                            </span>
                            <span className="schedule-time">{schedule.time} UTC</span>
                            <span className="schedule-prize">🏆 {schedule.prize}</span>
                        </div>
                        <button 
                            className="delete-btn"
                            onClick={() => handleDeleteSchedule(schedule.id)}
                        >
                            Delete
                        </button>
                    </div>
                ))}
            </div>

            {/* Add New Schedule */}
            <div className="add-schedule-form">
                <h3>Add New Raffle</h3>
                <div className="form-row">
                    <div className="form-group">
                        <label>Day of Week</label>
                        <select 
                            value={newSchedule.day} 
                            onChange={e => setNewSchedule({...newSchedule, day: parseInt(e.target.value)})}
                            className="styled-select"
                        >
                            <option value={0}>Sunday</option>
                            <option value={1}>Monday</option>
                            <option value={2}>Tuesday</option>
                            <option value={3}>Wednesday</option>
                            <option value={4}>Thursday</option>
                            <option value={5}>Friday</option>
                            <option value={6}>Saturday</option>
                        </select>
                    </div>
                    <div className="form-group">
                        <label>Time (UTC)</label>
                        <input 
                            type="time" 
                            value={newSchedule.time} 
                            onChange={e => setNewSchedule({...newSchedule, time: e.target.value})}
                            className="styled-input"
                        />
                    </div>
                </div>
                <div className="form-group">
                    <label>Prize Text</label>
                    <input 
                        type="text" 
                        value={newSchedule.prize} 
                        onChange={e => setNewSchedule({...newSchedule, prize: e.target.value})}
                        placeholder="$7 USD"
                        className="styled-input"
                    />
                </div>
                <button onClick={handleAddSchedule} disabled={loading} className="add-btn">
                    {loading ? 'Adding...' : '+ Add Raffle'}
                </button>
                {message && <p className="success-msg">{message}</p>}
            </div>
        </section>

        {/* Logs Section */}
        <section className="admin-section">
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px'}}>
                <h2 style={{marginBottom: 0}}>Winner Log</h2>
                <button 
                    onClick={handleClearLogs} 
                    className="delete-btn"
                    style={{fontSize: '0.9rem', padding: '8px 16px'}}
                >
                    Clear History
                </button>
            </div>
            <div className="logs-table-container">
                <table className="logs-table">
                    <thead>
                        <tr>
                            <th>Date</th>
                            <th>Wallet ID</th>
                            <th>Prize</th>
                            <th>NFTs</th>
                        </tr>
                    </thead>
                    <tbody>
                        {logs.map(log => (
                            <tr key={log.id}>
                                <td>{log.timestamp && log.timestamp._seconds ? new Date(log.timestamp._seconds * 1000).toLocaleString() : new Date(log.date).toLocaleString()}</td>
                                <td className="mono">{log.walletId}</td>
                                <td>{log.prize}</td>
                                <td>{log.nftCount}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </section>
      </div>
    </div>
  );
};

export default AdminDashboard;
